import type { Coordinate, Entity } from "game-common/models"
import { Mechanics } from "game-common/mechanics/mechanics"
import type { AnimatedSprite } from "pixi.js"

// Ground speed in pixels per second; walk cadence includes two successive 30% increases.
const REFERENCE_SPEED = 100
const WALK_CADENCE_MULTIPLIER = 1.69
// Cap the final cadence, including tuning, at three times the authored rate.
const MAX_RATE = 3
// Time constant of the measured ground speed: long enough to ride over uneven interpolation steps.
const SMOOTHING_MS = 200
// A stall longer than this, or a jump further than TELEPORT_DISTANCE, is not walking.
const MAX_SAMPLE_GAP_MS = 250
const TELEPORT_DISTANCE = 64
// A body easing to a stop never drops below this share of its gameplay speed, so a hop can't hang.
const MIN_GAMEPLAY_SHARE = 0.5
// Setting off, the first readings of the ground crossed come in low -- the drawn position easing into
// motion, a slow body's first small steps -- and a gait with no floor (a generated one) would barely
// move its legs while the body already slides. For this long after it sets off, such a pace keeps at
// least its `onsetShare` of the gameplay speed.
const ONSET_MS = 300
export const ONSET_SHARE = 0.6
// A pace that counts only moving time (`movingOnly`): a hold of up to this long between moves is
// still walking (interpolation waiting on the network); a longer one is a pause, left out of the speed.
const PAUSE_MS = 60
// The time the first move after a pause is taken to have taken.
const RESUME_FRAME_MS = 20

/**
 * Feet keep pace with the ground the body is DRAWN crossing. Gameplay speed overstates that for
 * NPCs (the server mover delivers ~71% of nominal, see MOVEMENT_EFFICIENCY; a follower is held to
 * its leader), so it seeds the cadence before the first measurement and optionally floors it after.
 */
export class WalkAnimationPace {
    private anchor?: { x: number; y: number; time: number }
    private groundSpeed?: number
    private rate = 1
    private settled = 0
    private authoredSpeeds = new WeakMap<AnimatedSprite, number>()
    /** When the measurement last started afresh (the body set off, or reappeared). */
    private measuringSince = -Infinity
    /** When the drawn position last changed. */
    private movedAt = -Infinity

    // Sheet animations need a minimum cadence to finish a hop. Generated Ember gaits have
    // a known stride and must be allowed to follow even a very slow follower or enemy -- once going.
    // `movingOnly`: a body that stops and goes often (an NPC's brain) is measured over the time it
    // moves, not averaged with its pauses -- averaged, it reads slower than it walks, and its legs
    // trail its body (it floats).
    constructor(private readonly minGameplayShare = MIN_GAMEPLAY_SHARE, private readonly onsetShare = 0, private readonly movingOnly = false) {}

    update(entity: Entity, now = performance.now()): void {
        this.measure(entity.location, now)
        const gameplay = Mechanics.entity.speed.effectiveSpeed(entity)
        const nominal = Number.isFinite(gameplay) && gameplay > 0 ? gameplay : 0
        const share = now - this.measuringSince < ONSET_MS ? Math.max(this.minGameplayShare, this.onsetShare) : this.minGameplayShare
        const speed = Math.max(this.groundSpeed ?? nominal, nominal * share)
        // A stopped entity leaves walk through the normal idle driver. Keep the cycle running
        // during that handoff rather than freezing an airborne frame.
        if (speed > 0) {
            this.rate = speed / REFERENCE_SPEED
            this.settled = speed
        }
    }

    /** Pixels per second the feet are keeping pace with; held through a stop, like the cadence. */
    get speed(): number {
        return this.settled
    }

    private measure({ x, y }: Coordinate, now: number): void {
        if (this.movingOnly) return this.measureMoving(x, y, now)
        const anchor = this.anchor
        const elapsed = anchor ? now - anchor.time : Infinity
        const distance = anchor ? Math.hypot(x - anchor.x, y - anchor.y) : 0
        if (elapsed > MAX_SAMPLE_GAP_MS || distance > TELEPORT_DISTANCE) {
            // First sight, a stop, a deferred stretch or a teleport: measure afresh from here.
            this.anchor = { x, y, time: now }
            this.groundSpeed = undefined
            this.measuringSince = now
            return
        }
        // No new position yet (interpolation waiting on the network): fold this frame into the
        // next step rather than read it as a stop.
        if (distance === 0 || elapsed <= 0) return

        const speed = (distance * 1000) / elapsed
        const blend = 1 - Math.exp(-elapsed / SMOOTHING_MS)
        this.groundSpeed = this.groundSpeed === undefined ? speed : this.groundSpeed + (speed - this.groundSpeed) * blend
        this.anchor = { x, y, time: now }
    }

    /** As measure, over moving time only: a pause past PAUSE_MS is left out of the time a move took. */
    private measureMoving(x: number, y: number, now: number): void {
        const anchor = this.anchor
        const distance = anchor ? Math.hypot(x - anchor.x, y - anchor.y) : 0
        const idle = now - this.movedAt
        if (!anchor || distance > TELEPORT_DISTANCE || (distance > 0 && idle > MAX_SAMPLE_GAP_MS)) {
            // First sight, a teleport, or setting off after a real stop: measure afresh from here.
            this.anchor = { x, y, time: now }
            this.groundSpeed = undefined
            this.measuringSince = now
            if (distance > 0 || !anchor) this.movedAt = now
            return
        }
        if (distance === 0 || now <= anchor.time) return
        const took = idle <= PAUSE_MS ? now - anchor.time : RESUME_FRAME_MS
        const speed = (distance * 1000) / took
        const blend = 1 - Math.exp(-took / SMOOTHING_MS)
        this.groundSpeed = this.groundSpeed === undefined ? speed : this.groundSpeed + (speed - this.groundSpeed) * blend
        this.anchor = { x, y, time: now }
        this.movedAt = now
    }

    apply(action: string, animation?: AnimatedSprite): void {
        if (!action.startsWith("walk-") || !animation) return
        if (!this.authoredSpeeds.has(animation)) {
            this.authoredSpeeds.set(animation, animation.animationSpeed)
        }
        // Always use the authored value: repeated updates must not compound the multiplier.
        animation.animationSpeed = this.authoredSpeeds.get(animation)! * Math.min(MAX_RATE, this.rate * WALK_CADENCE_MULTIPLIER)
    }
}
